import { makeAutoObservable } from "mobx";
import {
  ArticlesOfAssociationChangeMode,
  DbotEvent,
  DbotEventDispatcher,
  DecisionContext,
  extractSectionWithMaxScoreFromArticles,
  SharesForShareholder,
  total,
  updateArticlesModeModel,
} from "../common/commonDomain";
import { identity } from "lodash";
import { ExtendedShareholderDTO, ShareholderData } from "../../types/Shareholder";
import Big from "big.js";
import {
  Purpose,
  purposeCaptions,
} from "../../shareholderRegister/transactions/commonCapitalReduction";
import { Language } from "../../i18n/languages";

import { TMessage } from "../../i18n/tt";
import { LocalizedText, SpecialCircumstancesType } from "../../common/common";
import { DecisionType } from "../../common/decisions";
import {
  ArticleSelectorModel,
  BooleanFieldModel,
  LocalDateFieldModel,
  LocalizedStringFieldModel,
  NumberFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "../../common/commonForms";
import { ShareType, ShareTypeCode, shareTypeLabel } from "../../common/share";
import { ChangeArticlesOfAssociationModel } from "../changeArticlesOfAssociation/changeArticlesOfAssociationDomain";

export type AmountToBePaidOutPerShare = {
  sharetypeId: number;
  amount: number;
};

export type ReductionOfShareCapital = {
  type: DecisionType.reductionOfShareCapital;
  resolutionIsBasedOnIntermediateBalanceSheet: boolean;
  intermediateBalanceSheetDate?: string;
  reductionType?: ReductionTypeData;
  reductionPurposes: Purpose[];
  isSubsequentCapitalIncrease: boolean;
  amountToBePaidOutPerShare: AmountToBePaidOutPerShare[];
  isRepaymentOfPaidInCapital: boolean;
  estimatedCosts: number;
  coverOfLossAmount: number;
  transferToOtherFundsAmount: number;
  distributeToShareholdersAmount: number;
  cancellationOfOwnSharesAmount: number;
  updateArticlesAutomatically?: boolean;
  articleOfAssociationNumber: number;
  articleChanges: ArticlesOfAssociationChangeMode[];
  circumstances: SpecialCircumstancesType;
  circumstancesExplanation: LocalizedText[];
  circumstancesMaterialImportance?: SpecialCircumstancesType;
  circumstancesMaterialImportanceExplanation?: LocalizedText[];
  capitalReductionAccountsType: ShareCapitalReductionType;
  dateOfDetermination?: string;
};

export type ReductionTypeData = ReductionOfNominalValue | CancellationOfShares;

export type ReductionOfNominalValue = {
  type: ReductionType.reductionOfNominalValue;
  reductionInNominalValue: number;
};

export type CancellationOfShares = {
  type: ReductionType.cancellationOfShares;
  cancellationType?: CancellationType;
};

export type CancellationType = AllShares | AllSharesInShareClass | CertainShares;

export type AllShares = {
  type: CancellationMethod.ALL_SHARES;
};

export type AllSharesInShareClass = {
  type: CancellationMethod.ALL_SHARES_IN_SHARE_CLASS;
  sharetypeIds: number[];
};

export type CertainShares = {
  type: CancellationMethod.CERTAIN_SHARES;
  cancellationsForShareClasses: CancellationForShareClass[];
};

export class ReductionOfNominalValueModel {
  type: ReductionType.reductionOfNominalValue;
  reductionInNominalValue: NumberFieldModel;
  readonly currentNominalValue: number;
  private numberOfShares: number;

  constructor(currentNominalValue: number, numberOfShares: number, data?: ReductionTypeData) {
    this.type = ReductionType.reductionOfNominalValue;
    const value =
      data?.type === ReductionType.reductionOfNominalValue
        ? data.reductionInNominalValue
        : undefined;
    this.currentNominalValue = currentNominalValue;
    this.numberOfShares = numberOfShares;

    this.reductionInNominalValue = new NumberFieldModel({
      value: value,
      isPositive: true,
      required: true,
      validator: (value) => {
        const newNominalValue = Big(this.currentNominalValue)
          .minus(value ?? 0)
          .toNumber();
        return newNominalValue < 0
          ? { key: "reductionOfShareCapital.newNominalvalue.negative" }
          : undefined;
      },
    });
    makeAutoObservable(this);
  }

  calculateReduction(): number {
    return (
      Big(this.reductionInNominalValue.toNumberOrZero()).times(this.numberOfShares).toNumber() || 0
    );
  }

  toReductionOfNominalValue(): ReductionOfNominalValue {
    return {
      type: ReductionType.reductionOfNominalValue,
      reductionInNominalValue: this.reductionInNominalValue.toNumberOrZero(),
    };
  }

  get isValid() {
    return this.reductionInNominalValue.isValid;
  }

  validate() {
    this.reductionInNominalValue.validate();
    return this.isValid;
  }
}

export enum CancellationMethod {
  ALL_SHARES = "ALL_SHARES",
  ALL_SHARES_IN_SHARE_CLASS = "ALL_SHARES_IN_SHARE_CLASS",
  CERTAIN_SHARES = "CERTAIN_SHARES",
}

const cancellationMethodCaptions: { [key in CancellationMethod]: TMessage } = {
  [CancellationMethod.ALL_SHARES]: {
    key: "reductionOfShareCapital.cancellationAllShares",
  },
  [CancellationMethod.ALL_SHARES_IN_SHARE_CLASS]: {
    key: "reductionOfShareCapital.cancellationOfAllSharesInShareClass",
  },
  [CancellationMethod.CERTAIN_SHARES]: {
    key: "reductionOfShareCapital.cancellationOfCertainShares",
  },
};

export type CancellationForShareClass = {
  sharetypeId: number;
  numberOfSharesToBeCancelled: number;
  cancellationsForShareholders: CancellationForShareholder[];
};

export type CancellationForShareholder = {
  shareholderId: number;
  sharetypeId: number;
  numberOfSharesToBeCancelled: number;
  numberOfExistingShares: number;
};

export class CancellationForShareholderModel {
  readonly shareholderId: number;
  readonly shareholderName: string;
  readonly sharetypeId: number;
  readonly existingNumberOfShares: number;
  numberOfSharesToBeCancelled: NumberFieldModel;

  constructor(
    shareholderId: number,
    shareholderName: string,
    sharetypeId: number,
    existingNumberOfShares: number,
    numberOfSharesToBeCancelled: number,
  ) {
    this.shareholderId = shareholderId;
    this.shareholderName = shareholderName;
    this.sharetypeId = sharetypeId;
    this.existingNumberOfShares = existingNumberOfShares;
    this.numberOfSharesToBeCancelled = new NumberFieldModel({
      value: numberOfSharesToBeCancelled,
      isPositiveOrZero: true,
      isInteger: true,
    });
    makeAutoObservable(this);
  }
}

export class CancellationForShareClassModel {
  readonly sharetypeId: number;
  readonly sharetypeCode: ShareTypeCode;
  numberOfSharesToBeCancelled: NumberFieldModel;
  cancellationForShareholders: CancellationForShareholderModel[];
  isCancelDistributionValid = true;

  constructor(
    sharetypeId: number,
    sharetypeCode: ShareTypeCode,
    shareholders: ExtendedShareholderDTO[],
    currentShareForShareholders: SharesForShareholder[],
    cancellationForShareClass?: CancellationForShareClass,
  ) {
    const numberOfSharesToBeCancelled = cancellationForShareClass?.numberOfSharesToBeCancelled;
    this.sharetypeId = sharetypeId;
    this.sharetypeCode = sharetypeCode;
    this.numberOfSharesToBeCancelled = new NumberFieldModel({
      value: numberOfSharesToBeCancelled,
      isPositiveOrZero: true,
      isInteger: true,
    });
    this.cancellationForShareholders = shareholders
      .filter((s) => s.data.some((d) => d.shareTypeId === sharetypeId && d.numberOfShares > 0))
      .map(
        (shareholder) =>
          new CancellationForShareholderModel(
            shareholder.id,
            shareholder.name,
            sharetypeId,
            currentShareForShareholders
              .filter((d) => d.shareTypeId === sharetypeId && d.shareholderId === shareholder.id)
              .reduce((sum, shareholder) => sum + shareholder.numberOfShares, 0),
            cancellationForShareClass?.cancellationsForShareholders.find(
              (s) => s.sharetypeId === sharetypeId && s.shareholderId === shareholder.id,
            )?.numberOfSharesToBeCancelled ?? 0,
          ),
      )
      .filter((s) => s.existingNumberOfShares > 0);
    makeAutoObservable(this);
  }

  getCancellationForShareholder(shareholderId: number, sharetypeId: number) {
    return this.cancellationForShareholders.find(
      (c) => c.shareholderId === shareholderId && c.sharetypeId === sharetypeId,
    );
  }

  getTotal(): number {
    return this.cancellationForShareholders
      .filter((c) => c.sharetypeId === this.sharetypeId)
      .map((c) => c.numberOfSharesToBeCancelled.toNumberOrZero())
      .reduce((agg, a) => agg + a, 0);
  }

  validate() {
    this.isCancelDistributionValid =
      this.getTotal() === this.numberOfSharesToBeCancelled.toNumberOrZero();
    return this.isCancelDistributionValid;
  }
}

export class CancellationOfSharesModel {
  type: ReductionType.cancellationOfShares;
  decisionId: string;
  cancellationMethodModel: RadioFieldModel<CancellationMethod | undefined>;
  readonly data?: CancellationOfShares;
  readonly shareholderData: ShareholderData;
  cancellationsForShareClasses?: CancellationForShareClassModel[];
  isSpeficationDialogOpen = false;
  isLocked = false;
  cancellationOfAllSharesInShareClassModel: OptionFieldModel<number>;
  showAllCertainSharesCancellation = false;
  currentNominalValue: number;
  currentNumberOfShares: number;
  currentSharesForShareholder: SharesForShareholder[];

  constructor(
    readonly context: DecisionContext,
    data?: CancellationOfShares,
  ) {
    this.type = ReductionType.cancellationOfShares;
    this.decisionId = context.decisionId;
    this.isLocked = context.isLocked;
    this.currentNominalValue = context?.inputState?.sharenominalValue || 0;
    this.currentNumberOfShares = total(context?.inputState?.numberOfShares ?? []);
    this.shareholderData = context.shareholderData;
    this.currentSharesForShareholder = context.inputState?.sharesForShareholders || [];
    this.data = data;
    let selectedSharetypeIds: number[] = [];
    if (data?.cancellationType?.type === CancellationMethod.ALL_SHARES_IN_SHARE_CLASS) {
      selectedSharetypeIds = data.cancellationType.sharetypeIds;
    }

    this.cancellationOfAllSharesInShareClassModel = new OptionFieldModel<number>({
      value: selectedSharetypeIds,
      options: this.shareholderData.shareTypes.map((s) => s.sharetypeId),
      idFn: identity,
      labelFn: (value) => shareTypeLabel(this.sharetypeCodeForId(value)),
    });

    this.cancellationMethodModel = new RadioFieldModel<CancellationMethod | undefined>({
      required: true,
      value: this.data?.cancellationType?.type,
      idFn: identity,
      labelFn: (value) => (value ? cancellationMethodCaptions[value] : { translated: "" }),
      options: Object.values(CancellationMethod).filter(
        (c) => this.hasMultipleShareClasses || c !== CancellationMethod.ALL_SHARES_IN_SHARE_CLASS,
      ),
    });

    const cancellationType = this.data?.cancellationType;
    let cancellationsForShareClasses: CancellationForShareClass[];
    if (cancellationType?.type === CancellationMethod.CERTAIN_SHARES) {
      cancellationsForShareClasses = cancellationType.cancellationsForShareClasses;
    }
    this.cancellationsForShareClasses = this.shareholderData.shareTypes.map(
      (s) =>
        new CancellationForShareClassModel(
          s.sharetypeId,
          s.sharetypeCode,
          this.shareholderData.shareholders,
          this.currentSharesForShareholder,
          cancellationsForShareClasses?.find((c) => c.sharetypeId === s.sharetypeId),
        ),
    );
    makeAutoObservable(this);
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/update-input-state": {
        const inputState = this.context.inputState;
        this.currentNominalValue = inputState?.sharenominalValue || 0;
        this.currentNumberOfShares = total(inputState?.numberOfShares ?? []);
        break;
      }
    }
  }

  calculateReduction(): number {
    switch (this.cancellationMethodModel.value) {
      case undefined:
        return 0;
      case CancellationMethod.ALL_SHARES:
        return Big(this.currentNominalValue).times(this.currentNumberOfShares).toNumber() || 0;
      case CancellationMethod.ALL_SHARES_IN_SHARE_CLASS: {
        const numberOfShares = this.shareholderData.shareholders
          .flatMap((s) => s.data)
          .map((s) =>
            s.shareTypeId &&
            this.cancellationOfAllSharesInShareClassModel.selected.includes(s.shareTypeId)
              ? s.numberOfShares
              : 0,
          )
          .reduce((partial, a) => partial + a, 0);
        return Big(numberOfShares).times(this.currentNominalValue).toNumber() || 0;
      }
      case CancellationMethod.CERTAIN_SHARES: {
        const numberOfShares =
          this.cancellationsForShareClasses
            ?.flatMap((s) =>
              s.cancellationForShareholders.map((c) =>
                c.numberOfSharesToBeCancelled.toNumberOrZero(),
              ),
            )
            .reduce((partial, a) => partial + a, 0) ?? 0;
        return Big(numberOfShares).times(this.currentNominalValue).toNumber() || 0;
      }
    }
  }

  get isCancellationMethodCertainShares() {
    return this.cancellationMethodModel.value === CancellationMethod.CERTAIN_SHARES;
  }

  get isCancellationMethodAllSharesInShareClass() {
    return this.cancellationMethodModel.value === CancellationMethod.ALL_SHARES_IN_SHARE_CLASS;
  }

  get hasMultipleShareClasses(): boolean {
    return this.shareholderData.shareTypes.length > 1;
  }

  get sharesholders() {
    return this.shareholderData.shareholders.filter((s) =>
      s.data.some((d) => d.numberOfShares > 0),
    );
  }

  get shareClasses() {
    return this.shareholderData.shareTypes.map((s) => s.sharetypeCode);
  }

  get allShareClassesSelected() {
    return (
      this.shareClasses.length === this.cancellationOfAllSharesInShareClassModel.selected.length
    );
  }

  get displayPreviewOfCancellForShareClasses() {
    const certainShareholders = this.cancellationsForShareClasses
      ?.flatMap((c) => c?.cancellationForShareholders ?? [])
      .filter((s) => s.numberOfSharesToBeCancelled.toNumberOrZero() > 0);
    return (certainShareholders?.length ?? 0) > 5;
  }

  get cancellationForShareholdersForDisplay(): CancellationForShareholderModel[] {
    const certainShareholders = this.cancellationsForShareClasses
      ?.flatMap((c) => c?.cancellationForShareholders ?? [])
      .filter((s) => s.numberOfSharesToBeCancelled.toNumberOrZero() > 0);
    if (this.showAllCertainSharesCancellation) {
      return certainShareholders ?? [];
    } else {
      const cutOff = (certainShareholders?.length ?? 0) > 5;
      if (cutOff) {
        return certainShareholders?.slice(0, 3) ?? [];
      } else {
        return certainShareholders ?? [];
      }
    }
  }

  get companyOwnShareholderId(): number | undefined {
    return this.shareholderData.shareholders.find(
      (s) => s.isCompany && s.birthDateOrOrganisationNumber === s.organizationNumber,
    )?.id;
  }

  toggleShowAllCertainSharesCancellation() {
    this.showAllCertainSharesCancellation = !this.showAllCertainSharesCancellation;
  }

  sharetypeCodeForId(sharetypeId: number): ShareTypeCode {
    return (
      this.shareholderData.shareTypes.find((s) => s.sharetypeId === sharetypeId)?.sharetypeCode ??
      ShareTypeCode.UNKNOWN
    );
  }

  toggleSpecificationDialog() {
    this.isSpeficationDialogOpen = !this.isSpeficationDialogOpen;
  }

  validateCancelCertainSharesSpecification(): boolean {
    if (this.cancellationsForShareClasses?.length === 0) {
      return false;
    } else {
      return this.cancellationsForShareClasses?.every((c) => c.validate()) ?? false;
    }
  }

  saveCancelCertainSharesSpecification() {
    if (this.validateCancelCertainSharesSpecification()) {
      this.toggleSpecificationDialog();
    }
  }

  toCancellationOfSharesReduction(): CancellationOfShares {
    return {
      type: ReductionType.cancellationOfShares,
      cancellationType: this.toCancellationType(),
    };
  }

  toCancellationType(): CancellationType | undefined {
    switch (this.cancellationMethodModel.value) {
      case CancellationMethod.ALL_SHARES:
        return {
          type: CancellationMethod.ALL_SHARES,
        };
      case CancellationMethod.ALL_SHARES_IN_SHARE_CLASS:
        return {
          type: CancellationMethod.ALL_SHARES_IN_SHARE_CLASS,
          sharetypeIds: this.cancellationOfAllSharesInShareClassModel.selected,
        };
      case CancellationMethod.CERTAIN_SHARES:
        return {
          type: CancellationMethod.CERTAIN_SHARES,
          cancellationsForShareClasses:
            this.cancellationsForShareClasses?.map((c) => ({
              sharetypeId: c.sharetypeId,
              numberOfSharesToBeCancelled: c.numberOfSharesToBeCancelled.toNumberOrZero(),
              cancellationsForShareholders: c.cancellationForShareholders
                .filter((c) => c.numberOfSharesToBeCancelled.toNumberOrZero() > 0)
                .map((c) => ({
                  shareholderId: c.shareholderId,
                  shareholderName: c.shareholderName,
                  sharetypeId: c.sharetypeId,
                  numberOfSharesToBeCancelled: c.numberOfSharesToBeCancelled.toNumberOrZero(),
                  numberOfExistingShares: c.existingNumberOfShares,
                })),
            })) ?? [],
        };
    }
  }

  get isValid() {
    return this.cancellationMethodModel.isValid;
  }

  validate() {
    this.cancellationMethodModel.validate();
    return this.isValid;
  }
}

export enum ReductionType {
  reductionOfNominalValue = "reductionOfNominalValue",
  cancellationOfShares = "cancellationOfShares",
}

export enum ShareCapitalReductionType {
  ANNUAL_ACCOUNTS = "ANNUAL_ACCOUNTS",
  INTERIM_BALANCE_SHEET = "INTERIM_BALANCE_SHEET",
}

export type ReductionSummary = {
  reduction: number;
  specifiedPurposeAmount: number;
  addsUp: boolean;
};

const reductionTypeCaptions: { [key in ReductionType]: TMessage } = {
  [ReductionType.reductionOfNominalValue]: {
    key: "reductionOfShareCapital.reductionOfNominalValue",
  },
  [ReductionType.cancellationOfShares]: { key: "reductionOfShareCapital.cancellationOfShares" },
};

export const circumstancesCaptions: { [key in SpecialCircumstancesType]: TMessage } = {
  [SpecialCircumstancesType.NONE]: { key: "reductionOfShareCapital.NONE" },
  [SpecialCircumstancesType.NONE_UNKNOWN_TO_SUBSCRIBERS]: {
    key: "reductionOfShareCapital.NONE_UNKNOWN_TO_SUBSCRIBERS",
  },
  [SpecialCircumstancesType.ADDED_EXPLANATION]: {
    key: "reductionOfShareCapital.ADDED_EXPLANATION",
  },
};

export const shareCapitalReductionTypeLabels: { [key in ShareCapitalReductionType]: TMessage } = {
  [ShareCapitalReductionType.ANNUAL_ACCOUNTS]: {
    key: "reductionOfShareCapital.annualAccount",
  },
  [ShareCapitalReductionType.INTERIM_BALANCE_SHEET]: {
    key: "reductionOfShareCapital.interimBalanceSheet",
  },
};

export class AmountToBePaidOutPerShareModel {
  readonly sharetypeId: number;
  amountModel: NumberFieldModel;

  constructor(sharetypeId: number, data?: AmountToBePaidOutPerShare[]) {
    this.sharetypeId = sharetypeId;
    const amount = data?.find((d) => d.sharetypeId === sharetypeId)?.amount;
    this.amountModel = new NumberFieldModel({ value: amount, isPositiveOrZero: true });
    makeAutoObservable(this);
  }
}

export class ReductionOfShareCapitalForm {
  readonly type = DecisionType.reductionOfShareCapital;
  readonly dispatch: DbotEventDispatcher;
  readonly context: DecisionContext;
  readonly data?: ReductionOfShareCapital;
  readonly sharetypes: ShareType[];
  currentNominalValue: number;
  currentNumberOfShares: number;
  resolutionIsBasedOnIntermediateBalanceSheetModel: BooleanFieldModel;
  isSubsequentCapitalIncreaseModel: BooleanFieldModel;
  intermediateBalanceSheetDateModel: LocalDateFieldModel;
  reductionTypeRadioModel: RadioFieldModel<ReductionType | undefined>;
  reductionPurposeModel: OptionFieldModel<Purpose>;
  isRepaymentOfPaidInCapitalModel: BooleanFieldModel;
  reductionOfNominalValueModel: ReductionOfNominalValueModel;
  cancellationOfSharesModel: CancellationOfSharesModel;
  amountToBePaidOutPerShareModel: AmountToBePaidOutPerShareModel[];
  estimatedCostsModel: NumberFieldModel;
  coverOfLossAmountModel: NumberFieldModel;
  transferToOtherFundsModel: NumberFieldModel;
  distributeToShareholdersModel: NumberFieldModel;
  updateArticlesAutomatically: RadioFieldModel<boolean | undefined>;
  articleOfAssociationNumberModel: ArticleSelectorModel;
  articleChangeModel: ChangeArticlesOfAssociationModel;
  circumstancesModel: RadioFieldModel<SpecialCircumstancesType>;
  cirumstancesExplanationModel: LocalizedStringFieldModel[];
  circumstancesMaterialImportanceModel: RadioFieldModel<SpecialCircumstancesType>;
  circumstancesMaterialImportanceExplanationModel: LocalizedStringFieldModel[];
  annualAccountInterimBalanceSheetModel: RadioFieldModel<ShareCapitalReductionType>;
  dateOfDetermination: LocalDateFieldModel;

  constructor(
    dispatch: DbotEventDispatcher,
    context: DecisionContext,
    data?: ReductionOfShareCapital,
  ) {
    this.context = context;
    this.dispatch = dispatch;
    this.data = data;
    this.sharetypes = context.shareholderData.shareTypes;
    this.currentNominalValue = context?.inputState?.sharenominalValue || 0;
    this.currentNumberOfShares = total(context?.inputState?.numberOfShares ?? []);
    this.resolutionIsBasedOnIntermediateBalanceSheetModel = new BooleanFieldModel({
      value: data?.resolutionIsBasedOnIntermediateBalanceSheet ?? false,
    });

    this.intermediateBalanceSheetDateModel = new LocalDateFieldModel({
      value: data?.intermediateBalanceSheetDate,
    });
    this.reductionTypeRadioModel = new RadioFieldModel<ReductionType | undefined>({
      value: data?.reductionType?.type,
      options: Object.values(ReductionType),
      idFn: identity,
      labelFn: (value) => (value ? reductionTypeCaptions[value] : { translated: "" }),
      required: true,
    });
    this.reductionOfNominalValueModel = new ReductionOfNominalValueModel(
      this.currentNominalValue,
      this.currentNumberOfShares,
      data?.reductionType,
    );
    const cancellation: CancellationOfShares | undefined =
      data?.reductionType?.type === ReductionType.cancellationOfShares
        ? data?.reductionType
        : undefined;
    this.cancellationOfSharesModel = new CancellationOfSharesModel(context, cancellation);

    if (data?.reductionType) {
      this.setReductionType(data?.reductionType?.type);
    }
    this.reductionPurposeModel = new OptionFieldModel<Purpose>({
      value: data?.reductionPurposes,
      options: Object.values(Purpose),
      idFn: identity,
      labelFn: (value) => purposeCaptions[value],
      required: true,
    });
    this.isSubsequentCapitalIncreaseModel = new BooleanFieldModel({
      value: data?.isSubsequentCapitalIncrease ?? false,
    });
    this.amountToBePaidOutPerShareModel = this.sharetypes.map(
      (s) => new AmountToBePaidOutPerShareModel(s.sharetypeId, data?.amountToBePaidOutPerShare),
    );
    this.isRepaymentOfPaidInCapitalModel = new BooleanFieldModel({
      value: data?.isSubsequentCapitalIncrease ?? false,
    });
    this.estimatedCostsModel = new NumberFieldModel({
      value: data?.estimatedCosts,
      isPositiveOrZero: true,
    });
    this.coverOfLossAmountModel = new NumberFieldModel({
      value: data?.coverOfLossAmount,
      isPositiveOrZero: true,
    });
    this.transferToOtherFundsModel = new NumberFieldModel({
      value: data?.transferToOtherFundsAmount,
      isPositiveOrZero: true,
    });
    this.distributeToShareholdersModel = new NumberFieldModel({
      value: data?.distributeToShareholdersAmount,
      isPositiveOrZero: true,
    });

    this.updateArticlesAutomatically = updateArticlesModeModel(
      this.data?.updateArticlesAutomatically,
    );
    this.articleOfAssociationNumberModel = new ArticleSelectorModel(
      context.inputState?.articles,
      data?.articleOfAssociationNumber,
    );
    this.articleChangeModel = new ChangeArticlesOfAssociationModel(
      this.context,
      data?.articleChanges,
    );
    this.circumstancesModel = new RadioFieldModel<SpecialCircumstancesType>({
      value: data?.circumstances ?? SpecialCircumstancesType.NONE,
      options: Object.values(SpecialCircumstancesType),
      idFn: identity,
      labelFn: (value) => (value ? circumstancesCaptions[value] : { translated: "" }),
    });
    this.cirumstancesExplanationModel =
      data?.circumstancesExplanation?.map(
        (d) =>
          new LocalizedStringFieldModel(
            new StringFieldModel({
              value: d.text,
            }),
            d.language,
          ),
      ) ?? [];

    this.circumstancesMaterialImportanceModel = new RadioFieldModel<SpecialCircumstancesType>({
      value: data?.circumstancesMaterialImportance ?? SpecialCircumstancesType.NONE,
      options: Object.values(SpecialCircumstancesType),
      idFn: identity,
      labelFn: (value) => (value ? circumstancesCaptions[value] : { translated: "" }),
    });
    this.circumstancesMaterialImportanceExplanationModel =
      data?.circumstancesMaterialImportanceExplanation?.map(
        (d) =>
          new LocalizedStringFieldModel(
            new StringFieldModel({
              value: d.text,
            }),
            d.language,
          ),
      ) ?? [];
    this.annualAccountInterimBalanceSheetModel = new RadioFieldModel<ShareCapitalReductionType>({
      value: data?.resolutionIsBasedOnIntermediateBalanceSheet
        ? ShareCapitalReductionType.INTERIM_BALANCE_SHEET
        : ShareCapitalReductionType.ANNUAL_ACCOUNTS,
      options: Object.values(ShareCapitalReductionType),
      idFn: identity,
      labelFn: (value) => shareCapitalReductionTypeLabels[value],
    });
    this.dateOfDetermination = new LocalDateFieldModel({
      value: data?.dateOfDetermination,
      required: true,
    });
    makeAutoObservable(this, { dispatch: false }, { autoBind: true });
  }

  get isUpdateArticlesAutomatically() {
    return this.updateArticlesAutomatically.value === true;
  }

  get isUpdateArticlesManually() {
    return this.updateArticlesAutomatically.value === false;
  }

  init(): void {
    if (this.data === undefined || this.data.articleOfAssociationNumber === 0) {
      this.dispatch({
        type: "dbot/explore-articles-of-association",
        articles: this.context.inputState?.articles,
      });
    }
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/update-input-state": {
        const inputState = this.context.inputState;
        this.currentNominalValue = inputState?.sharenominalValue || 0;
        this.currentNumberOfShares = total(inputState?.numberOfShares ?? []);
        break;
      }
      case "dbot/explore-articles-of-association-success": {
        if (
          this.articleOfAssociationNumberModel.selected === undefined ||
          this.articleOfAssociationNumberModel.selected === 0
        ) {
          this.articleOfAssociationNumberModel.setSelected(
            extractSectionWithMaxScoreFromArticles(event.data.results, "SHARE_CAPITAL"),
          );
        }
        break;
      }
      default:
        this.cancellationOfSharesModel.handleEvent(event);
        break;
    }
  }

  get triggersSixWeeksCreditorsNotice(): boolean {
    const coverOfLoss = this.reductionPurposeModel.selected.includes(Purpose.COVER_OF_LOSS);
    if (!coverOfLoss) {
      return true;
    } else {
      /*for (const decision of this.context.currentDecisions.map((d) => d.decisionData)) {
        if (decision?.type === DecisionType.increaseOfShareCapital) {
          if (decision.properties.type === IncreaseOfShareCapitalType.increaseNominalValue) {
          }
          break;
        }
      }*/
      return false;
    }
  }

  circumstancesMaterialImportanceExplanationModelFor(language: Language): StringFieldModel {
    let localized = this.circumstancesMaterialImportanceExplanationModel.find(
      (d) => d.locale === language,
    );
    if (!localized) {
      localized = new LocalizedStringFieldModel(new StringFieldModel(), language);
      this.circumstancesMaterialImportanceExplanationModel.push(localized);
    }
    return localized.model;
  }

  cirumstancesExplanationModelFor(language: Language): StringFieldModel {
    let localized = this.cirumstancesExplanationModel.find((d) => d.locale === language);
    if (!localized) {
      localized = new LocalizedStringFieldModel(new StringFieldModel(), language);
      this.cirumstancesExplanationModel.push(localized);
    }
    return localized.model;
  }

  get mapCirumstancesExplanationModel(): LocalizedText[] {
    return this.cirumstancesExplanationModel.map((c) => ({
      language: c.locale,
      text: c.model.validatedValue,
    }));
  }

  get mapCircumstancesMaterialImportanceExplanationModel(): LocalizedText[] {
    return this.circumstancesMaterialImportanceExplanationModel.map((c) => ({
      language: c.locale,
      text: c.model.validatedValue,
    }));
  }

  get documentLanguages(): Language[] {
    return this.context.documentLanguages;
  }

  get calculateReductionInNominalvalueForPreview(): number {
    return Big(this.currentNominalValue)
      .minus(this.reductionOfNominalValueModel.reductionInNominalValue.toNumberOrZero())
      .toNumber();
  }

  get calculateCurrentSharecapitalForPreview(): number {
    return Big(this.currentNominalValue).times(this.currentNumberOfShares).toNumber();
  }

  get calculateSharecapitalAfterForPreview(): number {
    return Big(this.currentNominalValue)
      .minus(this.reductionOfNominalValueModel.reductionInNominalValue.toNumberOrZero())
      .times(this.currentNumberOfShares)
      .toNumber();
  }

  get calculateSharecapitalReductionAfterForPreview(): number {
    return Big(this.reductionOfNominalValueModel.reductionInNominalValue.toNumberOrZero())
      .times(this.currentNumberOfShares)
      .toNumber();
  }

  get isArticleChangesValid(): boolean {
    const isAutomaticArticleChangesValid =
      this.isUpdateArticlesAutomatically && this.articleOfAssociationNumberModel.isValid;
    const isManualArticleChangesValid =
      this.isUpdateArticlesManually && this.articleChangeModel.isValid;
    return isAutomaticArticleChangesValid || isManualArticleChangesValid;
  }

  get isValid(): boolean {
    return (
      this.reductionTypeRadioModel.isValid &&
      this.reductionPurposeModel.isValid &&
      this.dateOfDetermination.isValid &&
      (this.reductionTypeRadioModel.value !== ReductionType.reductionOfNominalValue ||
        this.reductionOfNominalValueModel.validate()) &&
      (this.reductionTypeRadioModel.value !== ReductionType.cancellationOfShares ||
        this.cancellationOfSharesModel.validate()) &&
      this.isArticleChangesValid
    );
  }

  validate() {
    this.reductionTypeRadioModel.validate();
    this.reductionPurposeModel.validate();
    this.dateOfDetermination.validate();
    if (this.reductionTypeRadioModel.value === ReductionType.reductionOfNominalValue) {
      this.reductionOfNominalValueModel.validate();
    }
    this.updateArticlesAutomatically.validate();
    this.articleChangeModel.validate();
    // if (this.reductionTypeRadioModel.value === ReductionType.cancellationOfShares) {
    //   this.cancellationOfSharesModel.validate();
    // }
    return this.isValid;
  }

  setReductionType(selected?: ReductionType) {
    this.reductionTypeRadioModel.setValue(selected);
  }

  getAmounModelForSharetype(sharetypeId: number): NumberFieldModel {
    return this.amountToBePaidOutPerShareModel.find((s) => s.sharetypeId === sharetypeId)!
      .amountModel;
  }

  get affectedShareTypes(): ShareType[] {
    if (this.isReductionOfNominalValue) {
      return this.sharetypes;
    } else if (this.isCancellationOfShares) {
      switch (this.cancellationOfSharesModel.cancellationMethodModel.value) {
        case CancellationMethod.ALL_SHARES:
          return this.sharetypes;
        case CancellationMethod.ALL_SHARES_IN_SHARE_CLASS:
          return this.sharetypes.filter((s) =>
            this.cancellationOfSharesModel.cancellationOfAllSharesInShareClassModel.selected.includes(
              s.sharetypeId,
            ),
          );
        case CancellationMethod.CERTAIN_SHARES: {
          const sharetypeIds = this.cancellationOfSharesModel.cancellationsForShareClasses?.flatMap(
            (s) => s.cancellationForShareholders.map((ss) => ss.sharetypeId),
          );
          return this.sharetypes.filter(
            (s) => sharetypeIds && sharetypeIds.includes(s.sharetypeId),
          );
        }
      }
    }
    return [];
  }

  get isReductionOfNominalValue(): boolean {
    return this.reductionTypeRadioModel.value === ReductionType.reductionOfNominalValue;
  }

  get isCancellationOfShares(): boolean {
    return this.reductionTypeRadioModel.value === ReductionType.cancellationOfShares;
  }

  toggleReductionPurpose(selected: Purpose) {
    this.reductionPurposeModel.toggle(selected);
  }

  get isDistributionToShareholders(): boolean {
    return this.reductionPurposeModel.selected.indexOf(Purpose.DISTRIBUTION) > -1;
  }

  get showCoverOfLossAmount(): boolean {
    return (
      this.reductionPurposeModel.selected.length > 1 &&
      this.reductionPurposeModel.selected.indexOf(Purpose.COVER_OF_LOSS) > -1
    );
  }

  get showSummaryForPurpose(): boolean {
    return this.reductionPurposeModel.selected.length > 1;
  }

  get showTransferToOtherFunds(): boolean {
    return (
      this.reductionPurposeModel.selected.length > 1 &&
      this.reductionPurposeModel.selected.indexOf(Purpose.TRANSFER) > -1
    );
  }

  get showDistributeToShareholders(): boolean {
    return (
      this.reductionPurposeModel.selected.length > 1 &&
      this.reductionPurposeModel.selected.indexOf(Purpose.DISTRIBUTION) > -1
    );
  }

  get calculateSpecifiedPurposeAmount(): number {
    if (this.reductionPurposeModel.selected.length < 2) {
      return 0;
    } else {
      let total = Big(0);
      if (this.reductionPurposeModel.selected.includes(Purpose.TRANSFER)) {
        total = total.plus(this.transferToOtherFundsModel.toNumberOrZero());
      }
      if (this.reductionPurposeModel.selected.includes(Purpose.DISTRIBUTION)) {
        total = total.plus(this.distributeToShareholdersModel.toNumberOrZero());
      }
      if (this.reductionPurposeModel.selected.includes(Purpose.COVER_OF_LOSS)) {
        total = total.plus(this.coverOfLossAmountModel.toNumberOrZero());
      }
      if (this.reductionPurposeModel.selected.includes(Purpose.CANCELLATION_OF_OWN)) {
        total = total.plus(this.calculateCancellationOfOwnSharesAmount);
      }
      return total.toNumber();
    }
  }

  get reductionSummary(): ReductionSummary {
    const reduction =
      (this.isReductionOfNominalValue
        ? this.reductionOfNominalValueModel
        : this.cancellationOfSharesModel
      ).calculateReduction() ?? 0;
    const specifiedPurposeAmount = this.calculateSpecifiedPurposeAmount;
    return {
      reduction: reduction,
      specifiedPurposeAmount: specifiedPurposeAmount,
      addsUp: specifiedPurposeAmount < 1 || reduction === specifiedPurposeAmount,
    };
  }

  get calculateCancellationOfOwnSharesAmount(): number {
    const totalNumberOfOwnShares = this.context.shareholderData.shareholders
      .filter((s) => s.isCompany && s.birthDateOrOrganisationNumber === s.organizationNumber)
      .flatMap((s) => s.data.map((c) => c.numberOfShares))
      .reduce((partial, a) => partial + a, 0);
    return Big(totalNumberOfOwnShares).times(this.currentNominalValue).toNumber();
  }

  get isAlsoDealingWithAnnualAccounts(): boolean {
    return (
      this.context.currentDecisions.find((d) => d.type === DecisionType.approveAnnualAccount) !==
      undefined
    );
  }

  toReductionType(): ReductionTypeData | undefined {
    if (this.isReductionOfNominalValue) {
      return this.reductionOfNominalValueModel.toReductionOfNominalValue();
    } else if (this.isCancellationOfShares) {
      return this.cancellationOfSharesModel.toCancellationOfSharesReduction();
    } else {
      return undefined;
    }
  }
}
